import React, { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  FlaskConical, 
  Download, 
  Clock, 
  Box, 
  Ruler, 
  AlertTriangle,
  CheckCircle,
  Thermometer,
  Layers,
  Zap,
  Grid3X3,
  FileCode,
  Copy
} from 'lucide-react';

// CNC Test Cut Generator
export const CNCTestCutModal = ({ 
  calculatedSettings, // From calculator
  material,
  toolDiameter,
  isOpen,
  onClose
}) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [formData, setFormData] = useState({
    material: material || 'pine',
    tool_diameter: toolDiameter || 6.35,
    feed_rate: calculatedSettings?.feed_rate || 1500,
    plunge_rate: calculatedSettings?.plunge_rate || 500,
    spindle_rpm: calculatedSettings?.spindle_rpm || 18000,
    pass_depth: calculatedSettings?.pass_depth || 2,
    material_thickness: 12,
    test_type: 'pocket'
  });
  
  const generateTestCut = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/calibration/cnc/test-cut`, formData);
      setResult(response.data);
      toast.success('Test cut G-code generated!');
    } catch (error) {
      toast.error('Failed to generate test cut');
    } finally {
      setLoading(false);
    }
  };
  
  const downloadGcode = () => {
    if (!result) return;
    const blob = new Blob([result.gcode], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `test_cut_${formData.material}_${formData.tool_diameter}mm.nc`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('G-code downloaded!');
  };
  
  const copyGcode = () => {
    if (!result) return;
    navigator.clipboard.writeText(result.gcode);
    toast.success('G-code copied to clipboard!');
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-700 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <FlaskConical className="w-5 h-5 text-orange-500" />
            Generate Test Cut
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Create a test pattern to validate your settings before committing to expensive materials
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          {!result ? (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300">Material</Label>
                  <Input
                    value={formData.material}
                    onChange={(e) => setFormData({...formData, material: e.target.value})}
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Material Thickness (mm)</Label>
                  <Input
                    type="number"
                    value={formData.material_thickness}
                    onChange={(e) => setFormData({...formData, material_thickness: parseFloat(e.target.value)})}
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  />
                </div>
              </div>
              
              <div className="p-4 bg-zinc-800/50 rounded-lg">
                <div className="text-sm text-zinc-400 mb-2">Settings to test:</div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Feed Rate:</span>
                    <span className="text-white">{formData.feed_rate} mm/min</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Spindle RPM:</span>
                    <span className="text-white">{formData.spindle_rpm}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Pass Depth:</span>
                    <span className="text-white">{formData.pass_depth} mm</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Tool Diameter:</span>
                    <span className="text-white">{formData.tool_diameter} mm</span>
                  </div>
                </div>
              </div>
              
              <Button 
                onClick={generateTestCut}
                disabled={loading}
                className="w-full bg-orange-600 hover:bg-orange-500"
              >
                {loading ? 'Generating...' : 'Generate Test Cut G-code'}
              </Button>
            </>
          ) : (
            <>
              <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-green-400 font-medium">Test Cut Ready!</span>
                </div>
                <p className="text-sm text-zinc-300">{result.description}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <Card className="bg-zinc-800/50 border-zinc-700">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-4 h-4 text-blue-400" />
                      <span className="text-sm text-zinc-400">Est. Time</span>
                    </div>
                    <div className="text-xl font-bold text-white">{result.estimated_time_minutes} min</div>
                  </CardContent>
                </Card>
                <Card className="bg-zinc-800/50 border-zinc-700">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Box className="w-4 h-4 text-purple-400" />
                      <span className="text-sm text-zinc-400">Material Needed</span>
                    </div>
                    <div className="text-sm font-medium text-white">{result.material_size_needed}</div>
                  </CardContent>
                </Card>
              </div>
              
              <div className="space-y-2">
                <Label className="text-zinc-300">Instructions:</Label>
                <div className="p-4 bg-zinc-800/50 rounded-lg space-y-2">
                  {result.instructions.map((inst, i) => (
                    <div key={i} className="text-sm text-zinc-300 flex items-start gap-2">
                      <span className="text-orange-500 font-mono">{inst.slice(0, 2)}</span>
                      <span>{inst.slice(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button onClick={downloadGcode} className="flex-1 bg-green-600 hover:bg-green-500">
                  <Download className="w-4 h-4 mr-2" />
                  Download .NC File
                </Button>
                <Button onClick={copyGcode} variant="outline" className="border-zinc-700">
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              
              <Button onClick={() => setResult(null)} variant="ghost" className="w-full">
                Generate Another
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};


// 3D Print Calibration Modal
export const Print3DCalibrationModal = ({ 
  material = 'PLA',
  nozzleTemp = 200,
  bedTemp = 60,
  isOpen,
  onClose
}) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [calibrationType, setCalibrationType] = useState('temp_tower');
  const [formData, setFormData] = useState({
    material: material,
    nozzle_temp_start: nozzleTemp + 20,
    nozzle_temp_end: nozzleTemp - 10,
    bed_temp: bedTemp,
    print_speed: 50,
    layer_height: 0.2,
    nozzle_diameter: 0.4
  });
  
  const calibrationTypes = [
    { value: 'temp_tower', label: 'Temperature Tower', icon: Thermometer, desc: 'Find optimal printing temperature' },
    { value: 'first_layer', label: 'First Layer Test', icon: Layers, desc: 'Calibrate bed leveling & Z-offset' },
    { value: 'retraction', label: 'Retraction Test', icon: Zap, desc: 'Eliminate stringing' },
    { value: 'flow', label: 'Flow Calibration', icon: Box, desc: 'Accurate dimensional prints' }
  ];
  
  const generateCalibration = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/calibration/3d-print/calibration`, {
        calibration_type: calibrationType,
        ...formData
      });
      setResult(response.data);
      toast.success('Calibration G-code generated!');
    } catch (error) {
      toast.error('Failed to generate calibration');
    } finally {
      setLoading(false);
    }
  };
  
  const downloadGcode = () => {
    if (!result) return;
    const blob = new Blob([result.gcode], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `calibration_${calibrationType}_${formData.material}.gcode`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('G-code downloaded!');
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-700 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <FlaskConical className="w-5 h-5 text-orange-500" />
            Print Calibration Tests
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Generate calibration prints to dial in your settings
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          {!result ? (
            <>
              <div className="grid grid-cols-2 gap-2">
                {calibrationTypes.map(type => (
                  <button
                    key={type.value}
                    onClick={() => setCalibrationType(type.value)}
                    className={`p-4 rounded-lg border text-left transition-all ${
                      calibrationType === type.value
                        ? 'border-orange-500 bg-orange-500/10'
                        : 'border-zinc-700 bg-zinc-800 hover:border-zinc-600'
                    }`}
                  >
                    <type.icon className={`w-5 h-5 mb-2 ${
                      calibrationType === type.value ? 'text-orange-500' : 'text-zinc-400'
                    }`} />
                    <div className="text-white font-medium">{type.label}</div>
                    <div className="text-xs text-zinc-500">{type.desc}</div>
                  </button>
                ))}
              </div>
              
              {calibrationType === 'temp_tower' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-300">Start Temp (°C)</Label>
                    <Input
                      type="number"
                      value={formData.nozzle_temp_start}
                      onChange={(e) => setFormData({...formData, nozzle_temp_start: parseInt(e.target.value)})}
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-300">End Temp (°C)</Label>
                    <Input
                      type="number"
                      value={formData.nozzle_temp_end}
                      onChange={(e) => setFormData({...formData, nozzle_temp_end: parseInt(e.target.value)})}
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    />
                  </div>
                </div>
              )}
              
              <Button 
                onClick={generateCalibration}
                disabled={loading}
                className="w-full bg-orange-600 hover:bg-orange-500"
              >
                {loading ? 'Generating...' : 'Generate Calibration Print'}
              </Button>
            </>
          ) : (
            <>
              <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-green-400 font-medium">Calibration Print Ready!</span>
                </div>
                <p className="text-sm text-zinc-300">{result.description}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <Card className="bg-zinc-800/50 border-zinc-700">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-4 h-4 text-blue-400" />
                      <span className="text-sm text-zinc-400">Est. Time</span>
                    </div>
                    <div className="text-xl font-bold text-white">{result.estimated_time_minutes} min</div>
                  </CardContent>
                </Card>
                <Card className="bg-zinc-800/50 border-zinc-700">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Box className="w-4 h-4 text-purple-400" />
                      <span className="text-sm text-zinc-400">Filament</span>
                    </div>
                    <div className="text-xl font-bold text-white">{result.filament_used_grams}g</div>
                  </CardContent>
                </Card>
              </div>
              
              <div className="space-y-2">
                <Label className="text-zinc-300">Instructions:</Label>
                <div className="p-4 bg-zinc-800/50 rounded-lg space-y-2">
                  {result.instructions.map((inst, i) => (
                    <div key={i} className="text-sm text-zinc-300">{inst}</div>
                  ))}
                </div>
              </div>
              
              <Button onClick={downloadGcode} className="w-full bg-green-600 hover:bg-green-500">
                <Download className="w-4 h-4 mr-2" />
                Download .gcode File
              </Button>
              
              <Button onClick={() => setResult(null)} variant="ghost" className="w-full">
                Generate Another
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};


// Laser Test Grid Modal
export const LaserTestGridModal = ({ 
  material,
  laserType = 'co2',
  laserPower = 40,
  isOpen,
  onClose
}) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [formData, setFormData] = useState({
    material: material || 'plywood',
    thickness: 3,
    laser_type: laserType,
    laser_power_watts: laserPower,
    mode: 'cut',
    power_start: 30,
    power_end: 100,
    power_step: 10,
    speed_start: 5,
    speed_end: 40,
    speed_step: 5
  });
  
  const generateTestGrid = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/calibration/laser/test-grid`, formData);
      setResult(response.data);
      toast.success('Test grid G-code generated!');
    } catch (error) {
      toast.error('Failed to generate test grid');
    } finally {
      setLoading(false);
    }
  };
  
  const downloadGcode = () => {
    if (!result) return;
    const blob = new Blob([result.gcode], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `laser_test_grid_${formData.material}.nc`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('G-code downloaded!');
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-700 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Grid3X3 className="w-5 h-5 text-orange-500" />
            Laser Test Grid
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Generate a power/speed test matrix to find optimal settings
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          {!result ? (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300">Material</Label>
                  <Input
                    value={formData.material}
                    onChange={(e) => setFormData({...formData, material: e.target.value})}
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Thickness (mm)</Label>
                  <Input
                    type="number"
                    value={formData.thickness}
                    onChange={(e) => setFormData({...formData, thickness: parseFloat(e.target.value)})}
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300">Mode</Label>
                  <Select 
                    value={formData.mode}
                    onValueChange={(v) => setFormData({...formData, mode: v})}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="cut">Cut</SelectItem>
                      <SelectItem value="engrave">Engrave</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-zinc-300">Laser Type</Label>
                  <Select 
                    value={formData.laser_type}
                    onValueChange={(v) => setFormData({...formData, laser_type: v})}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="co2">CO2 Laser</SelectItem>
                      <SelectItem value="diode">Diode Laser</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="p-4 bg-zinc-800/50 rounded-lg">
                <div className="text-sm text-zinc-400 mb-2">Test Grid Range:</div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-zinc-500 mb-1">Power Range</div>
                    <div className="text-white">{formData.power_start}% - {formData.power_end}%</div>
                  </div>
                  <div>
                    <div className="text-xs text-zinc-500 mb-1">Speed Range</div>
                    <div className="text-white">{formData.speed_start} - {formData.speed_end} mm/s</div>
                  </div>
                </div>
              </div>
              
              <Button 
                onClick={generateTestGrid}
                disabled={loading}
                className="w-full bg-orange-600 hover:bg-orange-500"
              >
                {loading ? 'Generating...' : 'Generate Test Grid'}
              </Button>
            </>
          ) : (
            <>
              <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-green-400 font-medium">Test Grid Ready!</span>
                </div>
                <p className="text-sm text-zinc-300">{result.description}</p>
              </div>
              
              <div className="p-4 bg-zinc-800/50 rounded-lg">
                <div className="text-sm text-zinc-400 mb-2">Grid Layout:</div>
                <pre className="text-xs text-zinc-300 font-mono overflow-x-auto">
                  {result.grid_layout}
                </pre>
              </div>
              
              <div className="p-4 bg-zinc-800/50 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-4 h-4 text-blue-400" />
                  <span className="text-sm text-zinc-400">Estimated Time:</span>
                  <span className="text-white">{result.estimated_time_minutes} min</span>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-zinc-300">Instructions:</Label>
                <div className="p-4 bg-zinc-800/50 rounded-lg space-y-2">
                  {result.instructions.map((inst, i) => (
                    <div key={i} className="text-sm text-zinc-300">{inst}</div>
                  ))}
                </div>
              </div>
              
              <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                  <span className="text-sm text-red-300">
                    Safety: Keep fire extinguisher ready. Never leave laser unattended.
                  </span>
                </div>
              </div>
              
              <Button onClick={downloadGcode} className="w-full bg-green-600 hover:bg-green-500">
                <Download className="w-4 h-4 mr-2" />
                Download Test Grid G-code
              </Button>
              
              <Button onClick={() => setResult(null)} variant="ghost" className="w-full">
                Generate Another
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};


// Time & Cost Estimator Component
export const TimeEstimator = ({ discipline, onEstimate }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  
  // CNC form
  const [cncForm, setCncForm] = useState({
    cut_length_mm: 1000,
    feed_rate: 1500,
    plunge_count: 10,
    plunge_rate: 500,
    total_plunge_depth_mm: 50,
    tool_changes: 0
  });
  
  // 3D Print form
  const [printForm, setPrintForm] = useState({
    model_volume_cm3: 20,
    infill_percent: 20,
    layer_height: 0.2,
    print_speed: 50,
    travel_speed: 150,
    filament_cost_per_kg: 25,
    filament_density: 1.24
  });
  
  // Laser form
  const [laserForm, setLaserForm] = useState({
    cut_length_mm: 500,
    speed: 20,
    passes: 1,
    engrave_area_mm2: 0,
    engrave_line_interval: 0.1,
    engrave_speed: 200
  });
  
  const estimate = async () => {
    setLoading(true);
    try {
      let endpoint, data;
      if (discipline === 'cnc') {
        endpoint = 'cnc';
        data = cncForm;
      } else if (discipline === '3d') {
        endpoint = '3d-print';
        data = printForm;
      } else {
        endpoint = 'laser';
        data = laserForm;
      }
      
      const response = await axios.post(`${API}/calibration/estimate/${endpoint}`, data);
      setResult(response.data);
      if (onEstimate) onEstimate(response.data);
    } catch (error) {
      toast.error('Failed to estimate');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Card className="bg-black/50 border-zinc-700/50">
      <CardHeader className="pb-2">
        <CardTitle className="text-white text-sm flex items-center gap-2">
          <Clock className="w-4 h-4 text-blue-400" />
          Time & Cost Estimator
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {discipline === 'cnc' && (
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-zinc-400">Cut Length (mm)</Label>
              <Input
                type="number"
                value={cncForm.cut_length_mm}
                onChange={(e) => setCncForm({...cncForm, cut_length_mm: parseFloat(e.target.value)})}
                className="bg-zinc-800 border-zinc-700 text-white text-sm h-8"
              />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Feed Rate (mm/min)</Label>
              <Input
                type="number"
                value={cncForm.feed_rate}
                onChange={(e) => setCncForm({...cncForm, feed_rate: parseFloat(e.target.value)})}
                className="bg-zinc-800 border-zinc-700 text-white text-sm h-8"
              />
            </div>
          </div>
        )}
        
        {discipline === '3d' && (
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-zinc-400">Model Volume (cm³)</Label>
              <Input
                type="number"
                value={printForm.model_volume_cm3}
                onChange={(e) => setPrintForm({...printForm, model_volume_cm3: parseFloat(e.target.value)})}
                className="bg-zinc-800 border-zinc-700 text-white text-sm h-8"
              />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Infill %</Label>
              <Input
                type="number"
                value={printForm.infill_percent}
                onChange={(e) => setPrintForm({...printForm, infill_percent: parseInt(e.target.value)})}
                className="bg-zinc-800 border-zinc-700 text-white text-sm h-8"
              />
            </div>
          </div>
        )}
        
        {discipline === 'laser' && (
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-zinc-400">Cut Length (mm)</Label>
              <Input
                type="number"
                value={laserForm.cut_length_mm}
                onChange={(e) => setLaserForm({...laserForm, cut_length_mm: parseFloat(e.target.value)})}
                className="bg-zinc-800 border-zinc-700 text-white text-sm h-8"
              />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Speed (mm/s)</Label>
              <Input
                type="number"
                value={laserForm.speed}
                onChange={(e) => setLaserForm({...laserForm, speed: parseFloat(e.target.value)})}
                className="bg-zinc-800 border-zinc-700 text-white text-sm h-8"
              />
            </div>
          </div>
        )}
        
        <Button 
          onClick={estimate}
          disabled={loading}
          size="sm"
          className="w-full bg-blue-600 hover:bg-blue-500"
        >
          {loading ? 'Calculating...' : 'Estimate Time'}
        </Button>
        
        {result && (
          <div className="p-3 bg-zinc-800/50 rounded-lg space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-zinc-400 text-sm">Estimated Time:</span>
              <span className="text-white font-bold">{result.estimated_time_formatted}</span>
            </div>
            {result.material_cost && (
              <div className="flex items-center justify-between">
                <span className="text-zinc-400 text-sm">Material Cost:</span>
                <span className="text-green-400 font-bold">${result.material_cost}</span>
              </div>
            )}
            {result.material_used && (
              <div className="flex items-center justify-between">
                <span className="text-zinc-400 text-sm">Material Used:</span>
                <span className="text-white text-sm">{result.material_used}</span>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
